import type {PersonalizationDraftRequest, PersonalizationRung} from "./personalizationDraft";
import type {PersonalizationAvailability} from "./personalizationOffer";
import type {PersonalizationRefusal} from "api/apiErrorIds";

export interface SuccessPersonalizationInput {
	transferId: string | undefined;
	personalized: boolean;
	expiresAtMs: number | null;
	now: number;
}

/**
 * Whether a sent transfer still admits a look the sender can compose.
 *
 * The delivery path is deliberately not an input. It used to be: only a hosted upload could be
 * personalized after the fact, because the only route that carried a late draft was the hosted
 * upload's fileless completion. `POST /transfer/{id}/personalization` (api#1592) accepts both, so a
 * peer-to-peer sender gets the same offer and there is no longer a mode to branch on.
 *
 * Nor is it only a FIRST look any more: what `personalized` means is answered by
 * {@link successPersonalizationSettled}, which since ui#2407 leaves the entry open after a free-only
 * save because the api lets its owner replace one (api#1628).
 */
export function successPersonalizationEntry(input: SuccessPersonalizationInput): boolean {
	return Boolean(input.transferId) && !input.personalized
		&& (input.expiresAtMs === null || input.now < input.expiresAtMs);
}

/**
 * Whether the look a transfer ALREADY carries closes the offer, from the RUNG it was attached at.
 * `null` is "nothing attached", and so is the `empty` rung, which reaches no api at all.
 *
 * ONE statement for the two moments a look can arrive (ui#2411). A look composed BEFORE the send rides
 * on the creation request; one composed after it goes through the transfer's own attach route. Both
 * leave the same transfer in the same state, so a sender who typed their name in the builder and then
 * pressed Send must keep the entry they would have kept by typing that name on the success card. The
 * flow used to ask a BOOLEAN of the send — whether a look rode along — which cannot tell those apart and
 * closed the offer on free copy that was never charged for.
 *
 * Only BRANDING closes it, and the reason is the plan rather than the api's write-once rule: a branded
 * look is not something the success card offers to change, while a free one needed no plan and its owner
 * may replace it while the transfer is live (api#1628).
 */
export function personalizationRungSettles(rung: PersonalizationRung | null): boolean {
	return rung === "branding";
}

/**
 * Whether the sender is DONE with this transfer's look — the `personalized` input above, decided once
 * rather than recomputed at the panel and again at the exposure counter.
 *
 * A save that carried BRANDING closes the offer, as it always has; a FREE-ONLY save does not (ui#2407,
 * owner live-testing 2026-09-12), because a sender who only signed their transfer must be able to come
 * back and add the look — the free-to-paid path the late entry exists for, and which closing here shut
 * off entirely. Which rung closes it is {@link personalizationRungSettles}, shared with the send.
 *
 * An EXPIRED send window closes the offer whatever was saved: there is no longer a live transfer to
 * attach anything to.
 */
export function successPersonalizationSettled(input: {
	stage: SuccessPersonalizationStage;
	/** The rung of the look the api accepted, or `null` while this screen has saved nothing. */
	savedRung: PersonalizationRung | null;
}): boolean {
	if (input.stage === "expired") {
		return true;
	}
	return input.stage === "saved" && personalizationRungSettles(input.savedRung);
}

/**
 * The words the late composer must START from, or `null` when there is nothing to carry over.
 *
 * The attach REPLACES a free snapshot rather than merging into it (api#1628), so a sender who signed
 * their transfer before pressing Send and then adds a look from the success card would send a request
 * carrying only the look — and the name they signed with would come off the recipient's page. Opening
 * that door (ui#2411) is what makes this necessary: the card's composer is a fresh one, which knows
 * nothing of the draft the create panel held.
 *
 * COPY only, because copy is all that can be here: a send that carried branding shows no entry at all,
 * so the attached look this seeds from is always free words. The heading and the body arrive folded
 * into one string — the fold is one-way and the composer re-folds an empty heading to the same bytes,
 * so what the transfer carries survives the round trip unchanged.
 */
export function successComposerSeed(
	attached: PersonalizationDraftRequest | null,
): {displayName: string; message: string} | null {
	if (attached === null) {
		return null;
	}
	const displayName = attached.displayName ?? "";
	const message = attached.message ?? "";
	return displayName === "" && message === "" ? null : {displayName, message};
}

/**
 * Where the success screen's late look is. There is no plan step between `submitted` and `saving`: every plan
 * includes the look (kitecorp/transferify#525).
 */
export type SuccessPersonalizationStage = "idle" | "submitted" | "saving" | "saved" | "failed" | "expired";

/** What pressing the success card's Personalise entry should do next. */
export type SuccessEntryPress = "replay" | "compose";

/**
 * Which of those a press means, from the flow's stage and whether an attach is still unaccounted for.
 *
 * `replay` re-sends the SNAPSHOT already on the wire rather than whatever the composer now holds. It is
 * for one situation only: an attach whose outcome this screen never learned, which may have landed —
 * re-sending the same bytes is the api's idempotent retry, while sending different ones would be a
 * second, different look.
 *
 * The retained snapshot alone is NOT that situation, and reading it as one is the ui#2407 defect: it
 * also exists after a save the api CONFIRMED, so the entry re-sent the accepted look instead of opening
 * the builder — which, now that a free look may be replaced, is the one thing the press must do. Asking
 * the stage as well is what tells "we do not know" from "we know it worked".
 */
export function successEntryPress(input: {
	stage: SuccessPersonalizationStage;
	/** Whether an attach was sent whose outcome this screen never learned. */
	unconfirmedAttach: boolean;
}): SuccessEntryPress {
	return input.stage === "failed" && input.unconfirmedAttach ? "replay" : "compose";
}

/**
 * What a failed attach leaves the success screen with.
 *
 * - `retry`: the outcome is unknown (a dropped connection, a 500), so the same snapshot is kept and the
 *   entry re-sends it: an identical draft is the api's idempotent retry.
 * - `refused`: the api named a refusal (`personalizationRefusalOf`), so re-sending the same snapshot would
 *   be refused again, a "Try again" that can never succeed. The snapshot is dropped, the screen says the
 *   look was not added, and the entry reopens the builder. An api from before kitecorp/transferify#525 still
 *   refuses a Free sender's look this way.
 *
 * Which sentence a refusal shows is the `notice`, a catalog key: `success.personaliseRefused` ("change it in
 * Personalise") where changing the look can help, and the refusal's own `errors.*` sentence for the two where it
 * cannot. A look already on the link cannot be changed, and only the sender may personalise; both sentences are
 * as true after the send as at it.
 */
export type SuccessRefusalNotice = "personaliseRefused" | "personalizationAlreadySet" | "personalizationNotTheSenders";

export type SuccessAttachFailure = {kind: "retry"} | {kind: "refused"; notice: SuccessRefusalNotice};

export function successAttachFailure(refusal: PersonalizationRefusal | null): SuccessAttachFailure {
	if (refusal === null) return {kind: "retry"};
	if (refusal === "personalizationAlreadySet" || refusal === "personalizationNotTheSenders") {
		return {kind: "refused", notice: refusal};
	}
	return {kind: "refused", notice: "personaliseRefused"};
}

export type SuccessPersonalizationAction =
	/**
	 * `blocked` is the draft the api would REFUSE, and it is its own answer rather than one of the two
	 * it used to be folded into (kitecorp/transferify-ui#2416).
	 *
	 * An animation whose pictures are missing makes `toPersonalizationRequest` answer null, so this
	 * flow read it as `empty` — "nothing composed" — went quietly back to idle and told the sender
	 * nothing: Done closed the builder, no dialog opened and no message appeared. `wait` is no
	 * better: waiting is for something that settles, and a refused draft never does on its own.
	 */
	| {kind: "empty" | "wait" | "blocked"}
	/**
	 * The rung travels WITH the request, because the flow has to remember what it actually ATTACHED.
	 * A retained snapshot is re-sent without consulting the draft again, and the draft may have moved
	 * on by then — so re-deriving the rung from the composer at the moment the save lands would decide
	 * whether the entry stays open from something other than what was saved.
	 */
	| {kind: "attach"; request: PersonalizationDraftRequest; rung: PersonalizationRung};

/**
 * What the success screen does with the look the sender just committed. Every plan includes the look
 * (kitecorp/transferify#525), so a look that can go out is attached as composed, with no plan step and no
 * credit.
 */
export function successPersonalizationAction(input: {
	request: PersonalizationDraftRequest | null;
	rung: PersonalizationRung;
	availability: PersonalizationAvailability;
	/** An offer read is still in flight. */
	pending: boolean;
	/**
	 * The armed draft names something the api would refuse — `usePersonalizationDraft`'s `blocksSend`.
	 *
	 * Asked AFTER `pending`, because an offer still loading publishes no arity and every draft looks
	 * refused for as long as that lasts. Asked BEFORE `request`, because a refused draft carries no
	 * request and would otherwise be indistinguishable from one the sender never composed.
	 */
	blocked: boolean;
}): SuccessPersonalizationAction {
	if (input.pending) return {kind: "wait"};
	if (input.blocked) return {kind: "blocked"};
	if (input.request === null) return {kind: "empty"};
	if (input.availability === "pending") return {kind: "wait"};
	return {kind: "attach", request: input.request, rung: input.rung};
}
